import axios from "axios";
import mergeWith from "lodash/mergeWith";
import moment from "moment";
import { getCurrentAccessToken } from "~/src/auth/manager";
import { TIMESHEETS_ENDPOINT } from "~/src/config";
import { APPROVAL_STATES, SALARIED_MEMBER_TYPES } from "~/src/consts";
import { SORTERS } from "~/src/utils/formatting";
import { isValidDate } from "~/src/utils/validators";
import { convertDateFromString } from "./util";

export const parseApprovals = (
  startTimeUpdates,
  endTimeUpdates,
  breakUpdates,
  ratingUpdates,
  noShowUpdates,
  turnedAwayUpdates,
  favouriteUpdates,
  timesheetBookings,
  feedbackLabelUpdates,
  feedbackUpdates,
  noteUpdates
) =>
  timesheetBookings.map(booking => ({
    id: booking.id,
    startOnApproval:
      startTimeUpdates[booking.id] || booking.bookingState?.startFinal,
    endOnApproval: endTimeUpdates[booking.id] || booking.bookingState?.endFinal,
    breakOnApproval: breakUpdates[booking.id],
    ratingOfMember: ratingUpdates[booking.id] || booking.ratingOfMember,
    isNoShow: noShowUpdates[booking.id] || booking.isNoShow,
    isTurnedAway: turnedAwayUpdates[booking.id] || booking.isTurnedAway,
    isFavourited: !!(
      favouriteUpdates[booking.id] || booking.ratingOfMember === 6
    ),
    feedbackLabelsForMember: feedbackLabelUpdates[booking.id],
    feedbackForMember: feedbackUpdates[booking.id],
    notes: noteUpdates[booking.id],
    showRating: booking.showRating
  }));

export const validateApprovals = ({
  bookingUpdates,
  isApprovingShift,
  isRatingRequired
}) => {
  let error = "";
  if (!bookingUpdates.length) error = "There are no bookings";
  bookingUpdates.forEach(approval => {
    if (
      !approval.ratingOfMember &&
      !approval.isNoShow &&
      !approval.isTurnedAway &&
      isApprovingShift &&
      isRatingRequired
    ) {
      error = "Please check that you have rated all members";
    } else if (
      approval.ratingOfMember <= 2 &&
      approval.ratingOfMember !== null &&
      isRatingRequired
    ) {
      if (
        !approval.feedbackLabelsForMember?.length &&
        !approval.feedbackForMember &&
        !approval.isNoShow &&
        !approval.isTurnedAway &&
        isRatingRequired
      ) {
        error =
          "You must provide feedback for all Members rated two stars or less.";
      }
    } else if (approval.isTurnedAway && !approval.notes) {
      error =
        'Please check you have given a reason to all members marked as "Turned away"';
    }
  });
  return error;
};

export const validateTurnedAway = (
  noteUpdates,
  turnedAwayUpdates,
  isApprovingShift
) => {
  let error = "";
  Object.entries(turnedAwayUpdates).find(([bookingId, val]) => {
    if (val && !noteUpdates[bookingId] && isApprovingShift) {
      error =
        'Please check you have given a reason to all members marked as "Turned away"';
      return true;
    }
    return false;
  });
  return error;
};

export const mergeNoShowTurnedAway = (noShowUpdates, turnedAwayUpdates) =>
  mergeWith(
    { ...noShowUpdates },
    { ...turnedAwayUpdates },
    (objValue, srcValue) => objValue || srcValue
  );

export const getIfValid = date =>
  date && isValidDate(date)
    ? moment(date, "DD/MM/YYYY")
        .toDate()
        .toString()
    : null;

export const arrangeTimesheets = timesheetArray => {
  const shiftsNeedingApproval = timesheetArray.filter(
    shift =>
      shift.reviewedAt === null &&
      shift.approvalState === APPROVAL_STATES.PENDING_ACCOUNT
  );

  const approvedShifts = timesheetArray.filter(
    shift => shift.approvalState !== APPROVAL_STATES.PENDING_ACCOUNT
  );

  return [
    ...shiftsNeedingApproval.sort(SORTERS.byDateDesc),
    ...approvedShifts.sort(SORTERS.byDateAsc)
  ];
};

export const removeNoBookings = shift => {
  const bookings = shift?.bookings?.data;

  // Keep cancellations where policy has been applied
  if (
    bookings.length >= 1 &&
    shift?.bookings?.data[0]?.result &&
    shift.approvalState === APPROVAL_STATES.NO_BOOKINGS
  ) {
    return shift.bookings.data.some(booking => booking.result.isPolicyApplied);
  }

  // Keep shifts where bookings have been applied
  if (
    bookings.length >= 1 &&
    shift.approvalState !== APPROVAL_STATES.NO_BOOKINGS
  ) {
    const totalShiftCost = bookings.reduce(
      (acc, booking) =>
        booking?.member?.annualSalary &&
        SALARIED_MEMBER_TYPES.includes(booking?.member?.memberType)
          ? 1
          : booking?.bookingState?.costFinal ?? 1,
      0
    );
    return totalShiftCost >= 1;
  }
  return false;
};

export const printAllShifts = async (
  allShiftPrint,
  picturesHidden,
  filters,
  shiftBookingCount,
  shiftBookingCountTotalCost
) => {
  const shiftData = allShiftPrint?.account?.shifts?.data;
  const shiftsWithBookings = shiftData?.filter(removeNoBookings);
  /*
    Generating in-between dates PDF timesheet via axios POST.
    Request: shift details for generating the timesheet.
    Response: ready-to-print Timesheet in PDF format in new window.
  */
  const isSafari =
    navigator.userAgent.indexOf("Safari") !== -1 &&
    navigator.userAgent.indexOf("Chrome") === -1;

  /*
    PROBLEM: Safari blocks window.open(url) when its called from async function.
    SOLUTION: Pre-opening a window before making the async call and pass the PDF.
  */
  let windowRef;
  if (isSafari) windowRef = window.open();
  if (!allShiftPrint) return null;

  await axios
    .post(
      TIMESHEETS_ENDPOINT,
      {
        shifts: shiftsWithBookings,
        bookingsTotalCount: shiftBookingCount,
        bookingsTotalCost: shiftBookingCountTotalCost,
        startDate: convertDateFromString(filters.startTime),
        endDate: convertDateFromString(filters.endTime),
        isHidden: picturesHidden,
        account: { logo: allShiftPrint?.account?.logo }
      },
      {
        headers: {
          Authorization: `Bearer ${await getCurrentAccessToken()}`
        },
        responseType: "arraybuffer"
      }
    )
    .catch(error => {
      if (error.response)
        console.log(
          "There was a problem with the server response:",
          console.log(error.response)
        );
      else if (error.request)
        console.log(
          "There was a problem with the request to the server:",
          console.log(error.request)
        );
      else console.log("Error:", console.log(error));
    })
    .then(res => {
      if (!res?.data) {
        return;
      }

      const blob = new Blob([res.data], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);
      if (isSafari) {
        windowRef.location = objectUrl;
      } else {
        window.open(objectUrl);
      }
    });
};
